import { useControlled } from '@dni-react-hooks/use-controlled'
import { useMergeRefs } from '@dni-react-hooks/use-merge-refs'
import { useOnClickAway } from '@dni-react-hooks/use-on-click-away'
import * as stylex from '@stylexjs/stylex'
import {
  Children,
  cloneElement,
  isValidElement,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentPropsWithoutRef,
  type ForwardedRef,
  type KeyboardEvent,
  type ReactElement,
  type ReactNode,
  type SyntheticEvent,
} from 'react'
import { Box } from '../Box/Box'
import { Button } from '../Button/Button'
import { HelpText, type HelpTextProps } from '../HelpText/HelpText'
import { ArrowDownSmall, ArrowUpSmall } from '../Icons'
import { Menu } from '../Menu/Menu'
import { type SelectOptionProps, type SelectOptionValue } from '../SelectOption/SelectOption'
import { Stack } from '../Stack/Stack'
import { Text } from '../Text/Text'
import { colors } from '../tokens/colors.stylex'
import type { ValueOf } from '../types/UtilityTypes'
import { useAnchorMatchedWidth } from '../useAnchorMatchedWidth/useAnchorMatchedWidth'
import { useXStyles, type XStyles } from '../useXStyles/useXStyles'
import { type ExtendStyleProps } from '../utils/makeExtendStyle/makeExtendStyle'
import { areEqualValues } from './areEqualValues'

export const SelectCloseReason = {
  TOGGLE: 'toggle',
  SELECT_OPTION: 'selectOption',
  ESCAPE: 'escape',
  CLICK_AWAY: 'clickAway',
  /** Tab 鍵離開（焦點即將移走，先收選單） */
  BLUR: 'blur',
} as const
export type SelectCloseReason = ValueOf<typeof SelectCloseReason>

const _baseStyles = stylex.create({
  root: {
    display: 'inline-flex',
    flexDirection: 'column',
    position: 'relative',
  },
  label: {
    color: colors.grey800,
  },
  helpText: {
    marginTop: 4,
  },
  select: {
    borderColor: {
      'default': colors.grey300,
      '@media (hover: hover)': {
        'default': null,
        ':hover:not(:disabled)': colors.grey800,
      },
    },
    backgroundColor: {
      'default': 'transparent',
      ':disabled': colors.grey200,
    },
    color: colors.grey500,
    justifyContent: 'space-between',
    textAlign: 'left',
  },
  hasValue: {
    color: colors.grey800,
  },
  error: {
    borderColor: colors.red600,
  },
  fullWidth: {
    width: '100%',
  },
  paper: {
    padding: 8,
    borderColor: colors.grey300,
    borderRadius: 4,
    borderStyle: 'solid',
    borderWidth: '1px',
    gap: 4,
    backgroundColor: colors.white,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
  },
  container: {
    gap: 4,
    display: 'flex',
    flexDirection: 'column',
  },
  endIcon: {
    color: colors.grey500,
  },
})

const dynamicStyles = stylex.create({
  width: (width: string | number) => ({ width }),
})

/*
 * 選單寬度的兩個下限只在「內容寬」模式有意義：對齊欄位寬時，paper 的 120 會把窄欄位的選單撐寬、
 * SelectOption 的 80 會在 paper 更窄時橫向溢出成捲軸，所以對齊模式下兩者都要讓開。
 */
const widthStyles = stylex.create({
  paperContentFloor: {
    minWidth: 120,
  },
  optionFluid: {
    minWidth: 0,
  },
})

export interface SelectProps<Value extends SelectOptionValue = SelectOptionValue> extends Omit<ComponentPropsWithoutRef<'div'>, 'placeholder' | 'onChange' | 'defaultValue'>, ExtendStyleProps {
  /**
   * 目前選取的值（受控模式）
   */
  value?: Value
  /**
   * 預設選取的值（非受控模式）
   */
  defaultValue?: Value
  /**
   * 是否禁用選擇器
   */
  disabled?: boolean
  /**
   * 是否顯示錯誤狀態
   */
  error?: boolean
  /**
   * 是否撐滿父容器寬度
   */
  fullWidth?: boolean
  /**
   * 下拉選單是否對齊欄位寬度。不傳時沿用 `fullWidth`。
   * 欄位寬度由內容決定（沒有 fullWidth）但仍希望選單對齊時傳 true——
   * 否則選單會用自己的內容寬，短選項比欄位窄、長選項比欄位寬。
   */
  matchAnchorWidth?: boolean
  /**
   * 是否展開下拉選單（受控模式）。
   * 受控時內部不再自行開合，須搭配 onOpen / onClose 由外部更新，否則選單會關不掉。
   */
  open?: boolean
  /**
   * 非受控模式的初始展開狀態
   * @default false
   */
  defaultOpen?: boolean
  /**
   * 選單展開時觸發
   */
  onOpen?: (event: SyntheticEvent) => void
  /**
   * 選單關閉時觸發。event 在 reason='clickAway' 時是 native Event（來自 document listener），其他來源是 React SyntheticEvent。
   */
  onClose?: (event: SyntheticEvent | Event, reason: SelectCloseReason) => void
  /**
   * SelectOption 子元件
   */
  children: ReactElement<SelectOptionProps>[] | ReactElement<SelectOptionProps>
  /**
   * 未選取時顯示的佔位文字
   * @default '請選擇'
   */
  placeholder?: ReactNode
  /**
   * 選擇器下方的說明或錯誤訊息
   */
  helpText?: ReactNode
  /**
   * 傳遞給 HelpText 元件的額外 props
   */
  HelpTextProps?: Omit<HelpTextProps, 'children'>
  /**
   * 選取值變更時的 callback
   */
  onChange?: (value: Value) => void
  /**
   * 自訂根元素樣式（StyleX）
   */
  rootStyle?: stylex.StyleXStyles
  /**
   * 覆寫內部子元素樣式
   */
  xStyles?: Partial<XStyles<keyof typeof _baseStyles>>
  /**
   * 根元素的 ref
   */
  rootRef?: ForwardedRef<HTMLDivElement>
  /**
   * 是否停用 Portal（將選單渲染在原位而非 body）
   */
  disablePortal?: boolean
  /**
   * 選擇器上方的標籤文字
   */
  label?: ReactNode
}

/**
 * Select 用於從一組選項中選取一個值，以下拉選單呈現。
 * 支援受控/非受控模式、禁用、錯誤狀態與標籤。
 * 選項透過 SelectOption 子元件提供。
 *
 * @example
 * ```tsx
 * <Select label="品牌" placeholder="請選擇" onChange={handleChange}>
 *   <SelectOption value="DCS">汪喵</SelectOption>
 *   <SelectOption value="HM">Hero mama</SelectOption>
 * </Select>
 * ```
 */
export const Select = <Value extends SelectOptionValue>(props: SelectProps<Value>) => {
  const {
    value: valueProp,
    defaultValue,
    disabled,
    error,
    fullWidth,
    matchAnchorWidth,
    open: openProp,
    defaultOpen = false,
    onOpen,
    onClose,
    placeholder = '請選擇',
    children,
    helpText,
    HelpTextProps,
    onChange,
    rootStyle,
    xStyles,
    rootRef: rootRefProp,
    disablePortal,
    label,
    ...rest
  } = props
  const baseStyles = useXStyles(_baseStyles, xStyles)

  const [value, setValue] = useControlled({
    controlled: valueProp,
    default: defaultValue,
  })

  const [menuElement, setMenuElement] = useState<HTMLDivElement | null>(null)
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const [open, setOpen] = useControlled({
    controlled: openProp,
    default: defaultOpen,
  })
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const shouldMatchAnchorWidth = matchAnchorWidth ?? !!fullWidth
  const menuWidth = useAnchorMatchedWidth(anchorEl, shouldMatchAnchorWidth)

  const rootRef = useRef<HTMLDivElement>(null)
  const mergeRef = useMergeRefs(rootRefProp, rootRef)

  const optionElements = useMemo(
    () => Children.toArray(children).filter(isValidElement) as ReactElement<SelectOptionProps>[],
    [children],
  )

  const handleOpen = (event: SyntheticEvent) => {
    if (open) return
    setOpen(true)
    onOpen?.(event)
  }

  const handleClose = (event: SyntheticEvent | Event, reason: SelectCloseReason) => {
    if (!open) return
    setOpen(false)
    onClose?.(event, reason)
  }

  const commitValue = (event: SyntheticEvent, newValue: Value) => {
    setValue(newValue)
    onChange?.(newValue)
    handleClose(event, SelectCloseReason.SELECT_OPTION)
  }

  const handleOnClick = (option: ReactElement<SelectOptionProps>): SelectOptionProps['onClick'] => (e, newValue) => {
    option.props.onClick?.(e, newValue)
    commitValue(e, newValue as Value)
  }

  const handleTriggerClick = (event: SyntheticEvent) => {
    if (open) {
      handleClose(event, SelectCloseReason.TOGGLE)
      return
    }
    handleOpen(event)
  }

  /*
   * 從 from 往 direction 方向找下一個非 disabled 選項，走到頭就繞回另一端。
   * 不用 modulo：from 允許 -1（無高亮）這個哨兵值，(-1 - 1 + total) % total 會落在
   * total - 2、跳過最後一項；逐步進位再邊界夾擠對所有哨兵值都正確。
   */
  const findNextEnabledIndex = (from: number, direction: 1 | -1) => {
    const total = optionElements.length
    if (total === 0) return -1
    let next = from
    for (let attempt = 0; attempt < total; attempt += 1) {
      next += direction
      if (next < 0) next = total - 1
      if (next >= total) next = 0
      if (!optionElements[next]?.props.disabled) return next
    }
    return -1
  }
  const findFirstEnabledIndex = () => findNextEnabledIndex(-1, 1)
  const findLastEnabledIndex = () => findNextEnabledIndex(optionElements.length, -1)

  /*
   * 高亮跟著開合走：開啟時從「目前選取的值」起跳（原生 select 的行為——沒有這步，
   * ArrowDown+Enter 會把已選值蓋成第一項），關閉時歸零。用 effect 而不是塞在
   * handleOpen/handleClose 裡，受控模式下外部直接翻 open prop 也能同步。
   */
  useEffect(function syncHighlightWithOpen() {
    if (!open) {
      setHighlightedIndex(-1)
      return
    }
    // 以 ArrowDown/Home 開啟時，鍵盤已在同一批更新設好初始高亮——只在沒有高亮時才從當前值起跳
    setHighlightedIndex(previous => previous >= 0
      ? previous
      : optionElements.findIndex(option => areEqualValues(option.props.value, value)))
    // optionElements 隨 children 每次 render 變動，依賴它會讓高亮被持續重設；開合當下的選項快照已足夠
  }, [open])

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp': {
        event.preventDefault()
        const direction = event.key === 'ArrowDown' ? 1 : -1
        if (!open) {
          handleOpen(event)
          setHighlightedIndex(direction === 1 ? findFirstEnabledIndex() : findLastEnabledIndex())
          return
        }
        setHighlightedIndex(findNextEnabledIndex(highlightedIndex, direction))
        return
      }
      case 'Home': {
        event.preventDefault()
        if (!open) handleOpen(event)
        setHighlightedIndex(findFirstEnabledIndex())
        return
      }
      case 'End': {
        event.preventDefault()
        if (!open) handleOpen(event)
        setHighlightedIndex(findLastEnabledIndex())
        return
      }
      case 'Enter':
      case ' ': {
        /*
         * 選單關閉時不攔也不開：交給 trigger button 的原生 activation（keydown → click →
         * handleTriggerClick）。在這裡 preventDefault 會取消原生 click，靠 root onClick
         * 冒泡運作的 consumer（如 buygather CartListSorter 的排序抽屜）鍵盤就打不開了。
         */
        if (!open) return
        // 開啟時攔下，否則原生 click 會接著 toggle、變成「選取 + 關閉再開」雙重動作
        event.preventDefault()
        const option = optionElements[highlightedIndex]
        if (!option || option.props.disabled) {
          // 沒有可提交的高亮（無值且未導航）：Enter 視為收合，對齊原生 select
          handleClose(event, SelectCloseReason.TOGGLE)
          return
        }
        commitValue(event, option.props.value as Value)
        return
      }
      case 'Escape': {
        if (!open) return
        event.preventDefault()
        handleClose(event, SelectCloseReason.ESCAPE)
        return
      }
      case 'Tab': {
        // 不攔 Tab：焦點該照常移到下一個元素，只把選單收起來
        handleClose(event, SelectCloseReason.BLUR)
        return
      }
      default: return
    }
  }

  const handleRootKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    // consumer 的 handler 先跑，preventDefault() 後跳過內建鍵盤行為
    rest.onKeyDown?.(event)
    if (event.defaultPrevented) return
    handleKeyDown(event)
  }

  const menuElementRef = useRef<HTMLDivElement | null>(null)
  menuElementRef.current = menuElement
  const clickAwayRefs = useMemo(() => [rootRef, menuElementRef], [rootRef])

  useOnClickAway({
    ref: clickAwayRefs,
    callback: (event) => {
      if (event) handleClose(event, SelectCloseReason.CLICK_AWAY)
    },
  })

  let display: ReactNode = null

  const options = optionElements.map((option, index) => {
    const optionValue = option.props?.value

    const isSelected = areEqualValues(optionValue, value)

    if (isSelected) {
      display = option.props.children
    }

    return cloneElement(option, {
      selected: option.props.selected ?? isSelected,
      // 已選項不畫高亮：開啟時高亮 seeding 落在已選項上，畫了會讓所有既有消費端
      // （含官網結帳的下拉）在開啟瞬間變色；selected 的底色本身已足以標示位置
      highlighted: index === highlightedIndex && !isSelected,
      onClick: handleOnClick(option),
      xStyles: shouldMatchAnchorWidth
        ? { ...option.props.xStyles, root: [widthStyles.optionFluid, option.props.xStyles?.root] }
        : option.props.xStyles,
    })
  })

  const customIconColor = xStyles?.endIcon ? undefined : colors.grey500

  return (
    <Box
      rootRef={mergeRef}
      rootStyle={[
        baseStyles.root,
        fullWidth && baseStyles.fullWidth,
        rootStyle,
      ]}
      {...rest}
      onKeyDown={handleRootKeyDown}
    >
      <Stack rootStyle={baseStyles.container}>
        {label && (
          <Text variant="body" rootStyle={baseStyles.label}>
            {label}
          </Text>
        )}
        <Button
          rootRef={setAnchorEl}
          onClick={handleTriggerClick}
          disabled={disabled}
          fullWidth={fullWidth}
          variant="outlined"
          color="grey"
          rootStyle={[
            baseStyles.select,
            // 顏色跟著實際顯示內容走：controlled 模式以空字串/null 表達未選擇，顯示 placeholder 時不可誤套已選樣式
            Boolean(display) && baseStyles.hasValue,
            error && baseStyles.error,
          ]}
          disableRipple
          endIcon={(
            open ? <ArrowUpSmall color={customIconColor} rootStyle={baseStyles.endIcon} /> : <ArrowDownSmall color={customIconColor} rootStyle={baseStyles.endIcon} />
          )}
        >
          <Text rootStyle={xStyles?.select}>
            {display || placeholder}
          </Text>
        </Button>
        <Menu
          open={open}
          anchorEl={anchorEl}
          disablePortal={disablePortal}
          rootStyle={shouldMatchAnchorWidth && dynamicStyles.width(menuWidth)}
          paperStyle={[!shouldMatchAnchorWidth && widthStyles.paperContentFloor, baseStyles.paper]}
          rootRef={setMenuElement}
        >
          {options}
        </Menu>
      </Stack>
      {
        helpText
        && (
          <HelpText
            error={error}
            {...HelpTextProps}
            rootStyle={[!HelpTextProps?.floating && baseStyles?.helpText, HelpTextProps?.rootStyle]}
          >
            {helpText}
          </HelpText>
        )
      }
    </Box>
  )
}
